import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { CASES } from "@/data/cases";
import { caseTexts } from "@/data/caseTexts";
import { useLanguage } from "@/i18n/LanguageContext";

const CardInner = ({ c }) => {
  const { t } = useLanguage();

  return (
  <>
    <div className="relative overflow-hidden rounded-2xl border border-white/10">
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={c.image}
          alt={`${c.title} — ${c.category}`}
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
      <span className="absolute left-6 top-6 font-syne text-sm uppercase tracking-[0.3em] text-white/70">
        {c.no}
      </span>
      {!c.demo && (
        <span className="absolute right-6 top-6 rounded-full border border-white/30 bg-black/40 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-white/80 backdrop-blur-md">
          {t("gallery.badge")}
        </span>
      )}
      <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#FF3333]">{c.category}</p>
          <h3 className="mt-1 font-syne text-3xl font-bold tracking-tight text-white">{c.title}</h3>
        </div>
        {c.demo && (
          <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/30 text-white transition-all duration-300 group-hover:border-[#FF3333] group-hover:bg-[#FF3333]">
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        )}
      </div>
    </div>
    <p className="mt-5 max-w-md text-sm leading-relaxed text-[#A3A3A3]">{c.description}</p>
  </>
  );
};

const CaseCard = ({ c }) =>
  c.demo ? (
    <Link to={`/demo/${c.slug}`} data-testid={`case-card-${c.slug}`} className="group block">
      <CardInner c={c} />
    </Link>
  ) : (
    <div data-testid={`case-card-${c.slug}`} className="group block">
      <CardInner c={c} />
    </div>
  );

export const Gallery = () => {
  const { t, lang } = useLanguage();
  const [activeCat, setActiveCat] = useState(null);
  const localized = CASES.map((c) => ({
    ...c,
    category: caseTexts[lang]?.[c.slug]?.category ?? c.category,
    description: caseTexts[lang]?.[c.slug]?.description ?? c.description,
  }));
  const categories = [...new Set(localized.map((c) => c.category))];
  const filtered = activeCat ? localized.filter((c) => c.category === activeCat) : localized;
  const pillId = (cat) => `filter-pill-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  return (
  <section id="cases" data-testid="gallery-section" className="mx-auto max-w-7xl px-6 py-32 md:px-12 md:py-48">
    <span className="text-sm uppercase tracking-[0.3em] text-[#FF3333]">{t("gallery.label")}</span>
    <h2 className="mt-4 font-syne font-extrabold uppercase tracking-tighter text-[#F2F2F2]">
      <MaskedLine innerClassName="text-4xl sm:text-5xl lg:text-6xl">{t("gallery.h1")}</MaskedLine>
      <MaskedLine delay={0.12} innerClassName="text-4xl sm:text-5xl lg:text-6xl text-stroke">
        {t("gallery.h2")}
      </MaskedLine>
    </h2>
    <FadeUp delay={0.2} className="mt-6 max-w-lg">
      <p className="text-base text-[#A3A3A3] md:text-lg">
        {t("gallery.intro")}
      </p>
    </FadeUp>

    <FadeUp delay={0.25}>
      <div data-testid="gallery-filters" className="mt-12 flex flex-wrap gap-3">
        <button
          data-testid="filter-pill-all"
          onClick={() => setActiveCat(null)}
          className={`rounded-full border px-5 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
            activeCat === null
              ? "border-[#FF3333] bg-[#FF3333] text-white"
              : "border-white/20 text-[#A3A3A3] hover:border-[#FF3333]/60 hover:text-[#F2F2F2]"
          }`}
        >
          {t("gallery.filterAll")} ({localized.length})
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            data-testid={pillId(cat)}
            onClick={() => setActiveCat(activeCat === cat ? null : cat)}
            className={`rounded-full border px-5 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
              activeCat === cat
                ? "border-[#FF3333] bg-[#FF3333] text-white"
                : "border-white/20 text-[#A3A3A3] hover:border-[#FF3333]/60 hover:text-[#F2F2F2]"
            }`}
          >
            {cat} ({localized.filter((c) => c.category === cat).length})
          </button>
        ))}
      </div>
    </FadeUp>

    <div className="mt-20 grid grid-cols-1 gap-x-12 gap-y-20 md:grid-cols-2">
      <div className="flex flex-col gap-20">
        {filtered.filter((_, i) => i % 2 === 0).map((c) => (
          <FadeUp key={c.slug}>
            <CaseCard c={c} />
          </FadeUp>
        ))}
      </div>
      <div className="flex flex-col gap-20 md:mt-32">
        {filtered.filter((_, i) => i % 2 === 1).map((c) => (
          <FadeUp key={c.slug} delay={0.1}>
            <CaseCard c={c} />
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
  );
};
